/**
 * RightSidebar: Note/obstacle tool grids,
 * selected note properties, minimap.
 * Matches Synth Riders editor layout.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useEditorStore } from '../../store/editorStore';
import type { ToolMode } from '../../types/beatmap';
import type { NoteFilterKind } from '../../store/areaSelection';
import { DEFAULT_DIFFICULTIES } from '../../types/beatmap';
import Minimap, { MinimapOptionsButton } from './Minimap';
import CustomDifficultyDialog from './CustomDifficultyDialog';
import BookmarkManagerDialog from './BookmarkManagerDialog';
import CollapsibleBox from './CollapsibleBox';
import AreaSelectionBox from './AreaSelectionBox';
import VisualizerPanel from '../../viz/VisualizerPanel';
import { usePanelLayout } from '../ui/panelLayoutStore';
import DifficultyBar from '../ui/DifficultyBar';
import { color, font } from '../ui/theme';
import { IcFullscreen, IcFullscreenExit, IcSettings, IcPanels, IcBulb, IcFlashEffect, IcBookmark, IcWave, IcObWallLeft, IcObWallCenter, IcObWallRight, IcObDiagLeft, IcObDiagRight, IcObCrouch, IcObSquare, IcObTriangle, IcShapes } from '../ui/icons';
import { groupStyle } from '../ui/editorSharedStyles';
import { TOOL_TO_WALL_KIND } from '../three/tunnelToolModes';

/** Circular header button — same look as the Flow editor's top-right buttons. */
function headerCircleBtn(active: boolean): React.CSSProperties {
  return {
    flexShrink: 0, width: 38, height: 38, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: active ? 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.2)' : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
    border: active ? '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)' : '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
    color: 'var(--accent)',
    cursor: 'pointer',
  };
}

// ── Tool Definitions ─────────────────────────────────────────────────────────

interface ToolDef {
  mode: ToolMode;
  label: string;
  icon: React.ReactNode; // a glyph string or an SVG icon element
  color: string;
}

const UTILITY_TOOLS: ToolDef[] = [
  { mode: 'select', label: 'Select', icon: '\u25C7', color: color.accentSoft },
  { mode: 'eraser', label: 'Eraser', icon: '\u2716', color: '#F472B6' },
];

// Note tool pairs: [hand, line] per row, matching the reference layout
interface NoteToolRow {
  hand: ToolDef;
  line: ToolDef;
}

function buildNoteToolRows(cL: string, cR: string, cO: string, cB: string): NoteToolRow[] {
  return [
    {
      hand: { mode: 'note_left', label: 'Left Hand', icon: '\u25CF', color: cL },
      line: { mode: 'rail_left', label: 'Rail', icon: '\u2550', color: cL },
    },
    {
      hand: { mode: 'note_right', label: 'Right Hand', icon: '\u25CF', color: cR },
      line: { mode: 'rail_right', label: 'Rail', icon: '\u2550', color: cR },
    },
    {
      hand: { mode: 'note_onehand', label: '\u2605 One Hand', icon: '\u25CF', color: cO },
      line: { mode: 'rail_onehand', label: 'Rail', icon: '\u2550', color: cO },
    },
    {
      hand: { mode: 'note_bothhands', label: '\u2605 Hands Together', icon: '\u25CF', color: cB },
      line: { mode: 'rail_bothhands', label: 'Rail', icon: '\u2550', color: cB },
    },
  ];
}

/** The sixth difficulty Synth VR always offers. Its key stays `Custom` in the file; the mapper's name is only a label. */
const CUSTOM_DIFFICULTY = 'Custom';

/** The note each hand button's Ctrl + click fills the area with. */
const FILL_KIND: Partial<Record<ToolMode, NoteFilterKind>> = {
  note_left: 'left_hand', note_right: 'right_hand', note_onehand: 'one_hand_special', note_bothhands: 'both_hands_special',
};

const OBSTACLE_TOOLS: ToolDef[] = [
  { mode: 'wall_left', label: 'Left', icon: <IcObWallLeft size={20} />, color: '#60A5FA' },
  { mode: 'wall_center', label: 'Center', icon: <IcObWallCenter size={20} />, color: '#FBBF24' },
  { mode: 'wall_right', label: 'Right', icon: <IcObWallRight size={20} />, color: '#F87171' },
  { mode: 'wall_diag_left', label: 'Diag L', icon: <IcObDiagLeft size={20} />, color: '#60A5FA' },
  { mode: 'crouch', label: 'Crouch', icon: <IcObCrouch size={20} />, color: '#9CA3AF' },
  { mode: 'wall_diag_right', label: 'Diag R', icon: <IcObDiagRight size={20} />, color: '#F87171' },
];

// Squares and triangles sit at the centre, so they share the Center wall's colour rather than
// adding a hue. Their own row, below the other six walls.
const CENTRE_WALL_COLOR = OBSTACLE_TOOLS.find(t => t.mode === 'wall_center')!.color;
const SQUARE_TRIANGLE_TOOLS: ToolDef[] = [
  { mode: 'wall_square', label: 'Square', icon: <IcObSquare size={20} />, color: CENTRE_WALL_COLOR },
  { mode: 'wall_triangle', label: 'Triangle', icon: <IcObTriangle size={20} />, color: CENTRE_WALL_COLOR },
];

const EFFECT_TOOLS: ToolDef[] = [
  { mode: 'flash_effect', label: 'Light', icon: '\u26A1', color: '#FBBF24' },
  { mode: 'bookmark', label: 'Bookmark', icon: '\u2691', color: color.accentSoft },
];

// ── Inject keyframe animation for gradient border ────────────────────────────

const GRADIENT_STYLE_ID = 'tool-btn-gradient-anim';

function injectGradientStyle() {
  if (document.getElementById(GRADIENT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = GRADIENT_STYLE_ID;
  style.textContent = `
    @keyframes tool-glow-spin {
      0%   { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .tool-btn-glow-wrap {
      position: relative;
      overflow: hidden;
      z-index: 0;
    }

    /* Spinning conic gradient behind the button — visible only at the edges */
    .tool-btn-glow-wrap::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: conic-gradient(
        var(--glow-color) 0deg,
        transparent 60deg,
        transparent 180deg,
        var(--glow-color) 240deg,
        transparent 300deg
      );
      animation: tool-glow-spin 2.5s linear infinite;
      z-index: -2;
      pointer-events: none;
    }

    /* Inner fill — masks the spinning gradient, leaving only a 1px animated border */
    .tool-btn-glow-wrap::after {
      content: '';
      position: absolute;
      inset: 1px;
      border-radius: inherit;
      background: var(--glow-fill, rgba(15, 15, 30, 0.95));
      z-index: -1;
      pointer-events: none;
    }
  `;
  document.head.appendChild(style);
}

// ── Toggle helper ────────────────────────────────────────────────────────────

function useToolToggle() {
  const toolMode = useEditorStore(s => s.toolMode);
  const setToolMode = useEditorStore(s => s.setToolMode);
  const setBookmarkDialogOpen = useEditorStore(s => s.setBookmarkDialogOpen);

  return (mode: ToolMode) => {
    if (toolMode === mode) {
      // Toggle off → go back to select (also close the bookmark manager if open).
      if (mode === 'bookmark') setBookmarkDialogOpen(false);
      setToolMode('select');
    } else {
      setToolMode(mode);
    }
  };
}

export default function RightSidebar({ scrollRef }: { scrollRef?: React.Ref<HTMLDivElement> }) {
  const toolMode = useEditorStore(s => s.toolMode);
  const toggleTool = useToolToggle();
  const isConsole = useEditorStore(s => s.activeView === 'console');
  // Synth VR: a wall button is an action, never a selected tool (SRVR-0313). It toggles or replaces
  // the one wall on the playhead's beat. Every other view (Console) keeps its selectable wall tools.
  const isSynthVR = useEditorStore(s => s.activeView === 'synth-rider');
  const toggleWallAtPlayhead = useEditorStore(s => s.toggleWallAtPlayhead);
  const wallButton = (mode: ToolMode) => isSynthVR
    ? { active: false, onClick: () => toggleWallAtPlayhead(TOOL_TO_WALL_KIND[mode]) }
    : { active: toolMode === mode, onClick: () => toggleTool(mode) };
  // Light and Bookmark likewise (SRVR-0324): in Synth VR a click adds or removes one at the playhead,
  // and neither is ever a selected tool. Console keeps them as placement tools.
  const toggleLightAtPlayhead = useEditorStore(s => s.toggleLightAtPlayhead);
  const toggleBookmarkAtPlayhead = useEditorStore(s => s.toggleBookmarkAtPlayhead);
  const effectButton = (mode: ToolMode) => isSynthVR
    ? { active: false, onClick: () => (mode === 'bookmark' ? toggleBookmarkAtPlayhead() : toggleLightAtPlayhead()) }
    : { active: toolMode === mode, onClick: () => toggleTool(mode) };
  // The Flash Effect (the game's `Effects`) is Synth VR's own button, an action like Light. It sits between
  // Light and Bookmark; the Console editor has no such button.
  const toggleEffectAtPlayhead = useEditorStore(s => s.toggleEffectAtPlayhead);
  const othersButtons: { mode: ToolMode | 'effects'; label: string }[] = isSynthVR
    ? [EFFECT_TOOLS[0], { mode: 'effects', label: 'Effects' }, EFFECT_TOOLS[1]]
    : EFFECT_TOOLS;
  // Synth VR Note Fill: Ctrl (or Cmd) + click on a note button picks that note type and fills the selected
  // area with it, one note per Step. A plain click is the tool toggle it always was.
  const setCustomDifficultyDialogOpen = useEditorStore(s => s.setCustomDifficultyDialogOpen);
  const setToolMode = useEditorStore(s => s.setToolMode);
  const fillAreaWithNotes = useEditorStore(s => s.fillAreaWithNotes);
  const handNoteClick = (e: React.MouseEvent, mode: ToolMode) => {
    const kind = isSynthVR && (e.ctrlKey || e.metaKey) ? FILL_KIND[mode] : undefined;
    if (!kind) { toggleTool(mode); return; }
    setToolMode(mode);
    // Holding Ctrl swaps in the Select tool until it is released; the picked type is what comes back.
    window.dispatchEvent(new CustomEvent('abm-ctrl-tool-picked', { detail: mode }));
    fillAreaWithNotes(kind);
  };
  const selectedNoteIds = useEditorStore(s => s.selectedNoteIds);
  const updateNote = useEditorStore(s => s.updateNote);
  const getCurrentDifficulty = useEditorStore(s => s.getCurrentDifficulty);
  const beatmap = useEditorStore(s => s.beatmap);
  const activeDifficulty = useEditorStore(s => s.activeDifficulty);
  const setActiveDifficulty = useEditorStore(s => s.setActiveDifficulty);
  const addDifficulty = useEditorStore(s => s.addDifficulty);
  const settingsOpen = useEditorStore(s => s.settingsOpen);
  const setSettingsOpen = useEditorStore(s => s.setSettingsOpen);
  const setUiHidden = useEditorStore(s => s.setUiHidden);
  const shapeDesignerOpen = useEditorStore(s => s.shapeDesignerOpen);
  const setShapeDesignerOpen = useEditorStore(s => s.setShapeDesignerOpen);
  const selectDesignShape = useEditorStore(s => s.selectDesignShape);
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void document.documentElement.requestFullscreen?.();
  }, []);
  // Track fullscreen so the button can show the exit icon while active.
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // One-time migration: dock the Minimap + Visualizer at the bottom of the right
  // sidebar (Visualizer just below the Minimap). Runs after the panels register
  // (child effects fire before this parent effect), so a previously-saved layout
  // — e.g. an early Visualizer that registered at order 0 — gets repositioned.
  useEffect(() => {
    const KEY = 'sr-viz-below-minimap-v1';
    if (localStorage.getItem(KEY)) return;
    const pl = usePanelLayout.getState();
    if (pl.get('sr', 'Minimap')) pl.movePanel('sr', 'Minimap', 'right', 999);
    if (pl.get('sr', 'Visualizer')) pl.movePanel('sr', 'Visualizer', 'right', 999);
    localStorage.setItem(KEY, '1');
  }, []);

  // Note colors from store
  const noteColorLeft = useEditorStore(s => s.gameplaySettings.noteColorLeft);
  const noteColorRight = useEditorStore(s => s.gameplaySettings.noteColorRight);
  const noteColorOneHand = useEditorStore(s => s.gameplaySettings.noteColorOneHand);
  const noteColorBothHands = useEditorStore(s => s.gameplaySettings.noteColorBothHands);
  const NOTE_TOOL_ROWS = buildNoteToolRows(noteColorLeft, noteColorRight, noteColorOneHand, noteColorBothHands);

  // UI container settings
  // Shared group-card style so all editors match Flow exactly (see LeftSidebar).
  const uiBoxStyle = groupStyle;

  // Inject the gradient animation CSS on mount
  useEffect(() => { injectGradientStyle(); }, []);

  const diff = getCurrentDifficulty();
  const selectedNotes = diff
    ? Array.from(selectedNoteIds).map(i => ({ index: i, note: diff.notes[i] })).filter(n => n.note)
    : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top-right action row — circular Fullscreen + Settings buttons, exactly
          like the Flow editor (no Play-on-phone — N/A for this editor). Song info
          now lives in the bottom playback bar. */}
      <div style={{ flexShrink: 0, padding: '0 11px 0 10px', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={() => setUiHidden(true)} title="Hide all panels (focus view)" style={headerCircleBtn(false)}>
          <IcPanels size={18} />
        </button>
        <button onClick={toggleFullscreen} title={isFullscreen ? 'Exit fullscreen' : 'Toggle fullscreen'} style={headerCircleBtn(false)}>
          {isFullscreen ? <IcFullscreenExit size={18} /> : <IcFullscreen size={18} />}
        </button>
        <button onClick={() => setSettingsOpen(!settingsOpen)} title="Editor settings" style={headerCircleBtn(settingsOpen)}>
          <IcSettings size={18} />
        </button>
      </div>
    <div ref={scrollRef} className="overlay-scroll" style={sidebarStyle}>

      {/* ── Difficulty ────────────────────────────────────── */}
      {/* Shows the difficulties present in the loaded map; click to switch the
          active one (with its note count). Empty until a map is loaded. */}
      <CollapsibleBox id="Difficulty" title="Difficulty" boxStyle={uiBoxStyle}>
        {!beatmap ? (
          <div style={diffEmptyStyle}>Load a map to see its difficulties</div>
        ) : (() => {
          // Always offer the 5 defaults; append any custom-named difficulties
          // present in the map. (VR has no Lanes variant — Flow only.)
          const customs = Object.keys(beatmap.difficulties).filter(
            d => !(DEFAULT_DIFFICULTIES as readonly string[]).includes(d),
          );
          // Synth VR has a sixth, permanent difficulty, Custom: its button shows the name the mapper gave it,
          // and Ctrl + click opens the panel that sets the name and speed (the desktop's F11).
          const all = isSynthVR ? [...DEFAULT_DIFFICULTIES, CUSTOM_DIFFICULTY, ...customs.filter(d => d !== CUSTOM_DIFFICULTY)] : [...DEFAULT_DIFFICULTIES, ...customs];
          const items = all.map(d => {
            const isCustomSlot = isSynthVR && d === CUSTOM_DIFFICULTY;
            const label = isCustomSlot ? (beatmap.custom_difficulty_name || 'Custom') : d;
            const count = beatmap.difficulties[d]?.note_count ?? 0;
            return {
              id: d,
              label,
              count,
              present: d in beatmap.difficulties,
              ...(isCustomSlot ? {
                tooltip: `${label} — ${count} notes. Ctrl + click to change its name and NJS.`,
                onCtrlClick: () => setCustomDifficultyDialogOpen(true),
              } : {}),
            };
          });
          return (
            <DifficultyBar
              items={items}
              activeId={activeDifficulty}
              onSelect={setActiveDifficulty}
              onCreate={addDifficulty}
              // Synth VR has its sixth difficulty built in, so it has no "+ Add".
              onAddCustom={isSynthVR ? undefined : () => {
                const n = window.prompt('New difficulty name:');
                if (n && n.trim()) addDifficulty(n.trim());
              }}
            />
          );
        })()}
      </CollapsibleBox>

      {/* ── Notes ─────────────────────────────────────────── */}
      <CollapsibleBox id="Notes" title="Notes" boxStyle={uiBoxStyle}>
        <div style={noteRowsContainer}>
          {NOTE_TOOL_ROWS.map(row => (
            <div key={row.hand.mode} style={noteRowStyle}>
              <NoteHandButton tool={row.hand} active={toolMode === row.hand.mode} onClick={e => handNoteClick(e, row.hand.mode)} />
              <NoteLineButton tool={row.line} active={toolMode === row.line.mode} onClick={() => toggleTool(row.line.mode)} />
            </div>
          ))}
        </div>
      </CollapsibleBox>

      {/* ── Area selection: the Z range and its Selection Options (VR only) ── */}
      {!isConsole && (
        <CollapsibleBox id="Selection" title="Selection" boxStyle={uiBoxStyle}>
          <AreaSelectionBox />
        </CollapsibleBox>
      )}

      {/* ── Obstacles Grid ───────────────────────────────── */}
      <CollapsibleBox id="Obstacles" title="Obstacles" boxStyle={uiBoxStyle}>
        <div style={toolGrid3Style}>
          {OBSTACLE_TOOLS.map(t => (
            <ToolButton key={t.mode} tool={t} {...wallButton(t.mode)} />
          ))}
        </div>
        {/* VR only: the Console variant shares this sidebar, and these tools are not part of it. */}
        {!isConsole && (
          <div style={toolGrid2Style} data-testid="square-triangle-tools">
            {SQUARE_TRIANGLE_TOOLS.map(t => (
              <ToolButton key={t.mode} tool={t} {...wallButton(t.mode)} />
            ))}
          </div>
        )}
        {/* Shapes panel toggle — sits just under the Obstacles grid. */}
        <button
          onClick={() => {
            const opening = !shapeDesignerOpen;
            setShapeDesignerOpen(opening);
            // Opening from the sidebar always lands on the Shapes LIST,
            // not a stale editor — clear the selection so the list view shows.
            if (opening) selectDesignShape(null);
          }}
          title="Shape Designer"
          style={{
            marginTop: 8, width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            padding: '7px 10px', borderRadius: 999, cursor: 'pointer',
            fontFamily: font.sans, fontSize: 11, fontWeight: 600,
            background: shapeDesignerOpen
              ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
              : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
            border: shapeDesignerOpen
              ? '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)'
              : '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.15)',
            color: shapeDesignerOpen ? 'var(--accent)' : color.accentSoft,
          } as React.CSSProperties}
        >
          <IcShapes size={14} />
          <span>Shape Designer</span>
        </button>
      </CollapsibleBox>

      {/* ── Others ──────────────────────────────────────────── */}
      <CollapsibleBox id="Others" title="Others" boxStyle={uiBoxStyle}>
        {/* Two separate pill containers (toggles) with minimalist SVG icons,
            like the Flow editor's visualizer toggle. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {othersButtons.map(t => {
            const { active, onClick } = t.mode === 'effects'
              ? { active: false, onClick: toggleEffectAtPlayhead }
              : effectButton(t.mode);
            return (
              <button
                key={t.mode}
                data-testid={`effect-button-${t.mode}`}
                onClick={onClick}
                style={{
                  // Synth VR has three buttons, which do not fit in one row: Bookmark takes a row of its own.
                  flex: isSynthVR && t.mode === 'bookmark' ? '1 1 100%' : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '6px 10px', borderRadius: 999, cursor: 'pointer',
                  fontFamily: font.sans, fontSize: 11, fontWeight: 600,
                  // Selected state matches the Transport buttons: accent gradient
                  // fill + accent border + accent text.
                  background: active
                    ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
                    : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
                  border: active ? '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)' : '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h)/0.15)',
                  color: active ? 'var(--accent)' : color.accentSoft,
                } as React.CSSProperties}
              >
                <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center' }}>
                  {t.mode === 'flash_effect' ? <IcBulb size={14} /> : t.mode === 'effects' ? <IcFlashEffect size={14} /> : <IcBookmark size={14} />}
                </span>
                <span style={{ position: 'relative', zIndex: 1 }}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </CollapsibleBox>

      {/* Visualizer panel — docks right below the Minimap (panel order 5 > 4). */}
      <CollapsibleBox id="Visualizer" title="Visualizer" boxStyle={uiBoxStyle}>
        <VisualizerPanel />
      </CollapsibleBox>

      {/* Flash settings live in the light's popup now (click a bulb), not here. */}

      {/* Selected Note Properties window removed per request. */}

      {/* ── Minimap — a dockable/draggable panel like the others. The ⚙ in the
             header opens its options (camera pose + zoom), same as Flow. ── */}
      <CollapsibleBox
        id="Minimap"
        title="Minimap"
        boxStyle={uiBoxStyle}
        headerAction={<MinimapOptionsButton />}
      >
        <Minimap />
      </CollapsibleBox>

      {/* ── Bookmark Manager Dialog (portal-like, fixed position) ── */}
      <BookmarkManagerDialog />
      {isSynthVR && <CustomDifficultyDialog />}
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function ToolButton({ tool, active, onClick, stretch, highlighted }: { tool: ToolDef; active: boolean; onClick: () => void; stretch?: boolean; highlighted?: boolean }) {
  const isLit = active || highlighted;
  // Selected state matches the Transport buttons: accent gradient fill + accent
  // border + accent text (no spinning glow).
  return (
    <button
      onClick={onClick}
      title={tool.label}
      aria-pressed={!!isLit}
      style={{
        ...toolBtnStyle,
        ...(stretch ? { flex: 1 } : {}),
        background: isLit
          ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
          : 'rgba(15, 15, 30, 0.4)',
        borderColor: isLit ? 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)' : 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.1)',
        color: isLit ? 'var(--accent)' : color.inkFaint,
        boxShadow: 'none',
      } as React.CSSProperties}
    >
      <span className="ic-themed" style={{ fontSize: 12, lineHeight: 1, position: 'relative', zIndex: 1 }}>{tool.icon}</span>
      <span style={{ fontSize: 8, lineHeight: 1, position: 'relative', zIndex: 1 }}>{tool.label}</span>
    </button>
  );
}

function opacityToHex(opacity: number): string {
  return Math.round(Math.max(0, Math.min(1, opacity)) * 255).toString(16).padStart(2, '0').toUpperCase();
}

function NoteHandButton({ tool, active, onClick }: { tool: ToolDef; active: boolean; onClick: (e: React.MouseEvent) => void }) {
  const iface = useEditorStore(s => s.interfaceSettings);
  const bgHex = opacityToHex(iface.toolBtnBgOpacity);
  const outHex = opacityToHex(iface.toolBtnOutlineOpacity);
  const lines = tool.label.split('\n');
  return (
    <button
      onClick={onClick}
      title={tool.label.replace('\n', ' ')}
      className={active ? 'tool-btn-glow-wrap' : undefined}
      style={{
        ...noteHandBtnStyle,
        '--glow-color': '#fff',
        '--glow-fill': tool.color,
        background: active
          ? tool.color
          : `${tool.color}${bgHex}`,
        borderWidth: iface.toolBtnOutlineWidth,
        borderColor: active ? 'transparent' : `${tool.color}${outHex}`,
        color: active ? '#000' : '#fff',
        boxShadow: active ? `0 0 12px ${tool.color}80` : `0 2px 4px rgba(0,0,0,0.3)`,
      } as React.CSSProperties}
    >
      {lines.map((l, i) => (
        <span key={i} style={{ display: 'block', lineHeight: 1.2, position: 'relative', zIndex: 1 }}>{l}</span>
      ))}
    </button>
  );
}

function NoteLineButton({ tool, active, onClick }: { tool: ToolDef; active: boolean; onClick: () => void }) {
  const iface = useEditorStore(s => s.interfaceSettings);
  const bgHex = opacityToHex(iface.toolBtnBgOpacity);
  const outHex = opacityToHex(iface.toolBtnOutlineOpacity);
  return (
    <button
      onClick={onClick}
      title={`${tool.label}`}
      className={active ? 'tool-btn-glow-wrap' : undefined}
      style={{
        ...noteLineBtnStyle,
        '--glow-color': '#fff',
        '--glow-fill': tool.color,
        background: active
          ? tool.color
          : `${tool.color}${bgHex}`,
        borderWidth: iface.toolBtnOutlineWidth,
        borderColor: active ? 'transparent' : `${tool.color}${outHex}`,
        color: active ? '#000' : '#fff',
        boxShadow: active ? `0 0 12px ${tool.color}80` : `0 2px 4px rgba(0,0,0,0.3)`,
      } as React.CSSProperties}
    >
      <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center' }}>
        <IcWave size={26} sw={2.2} className="ic-white" />
      </span>
    </button>
  );
}

function SectionLabel({ text }: { text: string }) {
  const iface = useEditorStore(s => s.interfaceSettings);
  return (
    <div style={{
      color: iface.typoTitleColor,
      fontSize: iface.typoTitleSize,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 1.2,
      textAlign: 'center',
      marginBottom: iface.typoTitleMarginBottom,
      fontFamily: font.sans,
    }}>
      {text}
    </div>
  );
}

function Divider() {
  return (
    <div style={{
      height: 1,
      background: 'linear-gradient(90deg, transparent, oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.2), transparent)',
      margin: '6px 0',
    }} />
  );
}

function PropRow({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
      <span style={{ color: color.inkFaint, fontSize: 10, minWidth: 32, fontWeight: 500 }}>{label}</span>
      {children || (
        <span style={{
          color: 'oklch(0.82 calc(0.12 * var(--accent-c)) var(--accent-h))',
          fontSize: 10,
          fontFamily: font.sans,
        }}>{value}</span>
      )}
    </div>
  );
}

function ColorRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
      <span style={{ color: color.inkFaint, fontSize: 10, minWidth: 50, fontWeight: 500 }}>{label}</span>
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ width: 28, height: 20, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
      />
      <span style={{ color: color.accentSoft, fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
    </div>
  );
}

function SliderRow({ label, value, min, max, step, suffix, onChange }: {
  label: string; value: number; min: number; max: number; step: number; suffix?: string;
  onChange: (v: number) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
      <span style={{ color: color.inkFaint, fontSize: 10, minWidth: 50, fontWeight: 500 }}>{label}</span>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ flex: 1, height: 4, accentColor: color.accentSoft }}
      />
      <span style={{ color: color.accentSoft, fontSize: 9, fontFamily: "'JetBrains Mono', monospace", minWidth: 32, textAlign: 'right' }}>
        {value.toFixed(2)}{suffix || ''}
      </span>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const sidebarStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  // Match Flow's right aside: gap 8 + mirrored H padding, no vertical padding.
  // Inner scroller (the SongTag header lives outside); fills remaining height.
  // Edge fade applied dynamically by <ScrollFade>.
  gap: 8,
  padding: '0 11px 0 10px',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  fontSize: 11,
};

// Difficulty grid/button/count styles now live in the shared DifficultyBar.

const diffEmptyStyle: React.CSSProperties = {
  fontSize: 10,
  color: color.inkFaint,
  padding: '4px 2px',
  lineHeight: 1.4,
};

const noteRowsContainer: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBottom: 8,
};

const noteRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  alignItems: 'stretch',
};

const noteHandBtnStyle: React.CSSProperties = {
  flex: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '5px 8px',
  borderRadius: 999,
  border: '2px solid transparent',
  cursor: 'pointer',
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 11,
  color: '#fff',
  transition: 'all 0.15s ease',
  minHeight: 30,
};

const noteLineBtnStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '5px 8px',
  borderRadius: 999,
  border: '2px solid transparent',
  cursor: 'pointer',
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 11,
  color: '#fff',
  transition: 'all 0.15s ease',
  minHeight: 30,
};

const toolGrid3Style: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 3,
  marginBottom: 4,
};

const toolGrid2Style: React.CSSProperties = { ...toolGrid3Style, gridTemplateColumns: 'repeat(2, 1fr)' };

const toolBtnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  padding: '5px 2px',
  borderRadius: 12,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.1)',
  cursor: 'pointer',
  fontFamily: font.sans,
  fontWeight: 500,
  transition: 'all 0.15s ease',
  minHeight: 34,
};

const propertiesBox: React.CSSProperties = {
  background: 'rgba(15, 15, 30, 0.3)',
  borderRadius: 12,
  padding: 8,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.08)',
  marginBottom: 6,
};

const selectStyle: React.CSSProperties = {
  background: 'rgba(15, 15, 30, 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  color: color.ink,
  borderRadius: 4,
  padding: '2px 4px',
  fontSize: 10,
  fontFamily: font.sans,
  flex: 1,
};
